import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { uploadFilePublic } from '../../../services/fileApi';
import {
  downloadPublicEventTicketProformaPdf,
  downloadPublicEventTicketReceiptPdf,
  downloadPublicEventTicketTaxInvoicePdf,
  downloadPublicEventTicketInvoicePdf,
  getPublicEventTicketPayment,
  initiatePublicEventTicketCardPayment,
  submitPublicEventTicketOfflinePayment,
} from '../../../services/eventApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { formatTicketMoney } from '../../../utils/eventTicketMoney';
import TicketCodeQr from './TicketCodeQr';
import {
  PaymentPendingApprovalCard,
  PaymentRejectedCard,
} from '../Common/eventPublicPaymentUi';

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function todayInputValue() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const PublicEventTicketPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payMode, setPayMode] = useState('bank');
  const [form, setForm] = useState({
    method: 'BANK_TRANSFER',
    referenceNumber: '',
    paymentDate: todayInputValue(),
    notes: '',
  });
  const [receipt, setReceipt] = useState(null);
  const [receiptMeta, setReceiptMeta] = useState(null);
  const [applyCredit, setApplyCredit] = useState(false);
  const [creditToApply, setCreditToApply] = useState(0);
  const [customDeposit, setCustomDeposit] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getPublicEventTicketPayment(token);
      setData(res.data?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Invalid payment link');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  useEffect(() => {
    const paycorp = searchParams.get('paycorp');
    if (paycorp === 'success') toast.success('Card payment successful — tickets issued');
    if (paycorp === 'failed') toast.error('Card payment failed');
    if (paycorp === 'cancelled') toast('Card payment cancelled');
  }, [searchParams]);

  const paid = String(data?.paymentStatus || '').toUpperCase() === 'PAID';
  const pending = String(data?.paymentStatus || '').toUpperCase() === 'PENDING_APPROVAL';
  const rejected = String(data?.paymentStatus || '').toUpperCase() === 'REJECTED';
  const linkActive = String(data?.paymentLinkStatus || '').toUpperCase() === 'ACTIVE';
  const canShowPaymentForm = !paid && !pending && linkActive;
  const linkExpired = !paid && !pending && String(data?.paymentLinkStatus || '').toUpperCase() === 'EXPIRED';

  const invoice = data?.performaInvoice || {};
  const subTotal = Number(invoice.subTotal ?? 0);
  const taxTotal = Number(invoice.taxTotal ?? 0);
  const grandTotal = Number(invoice.grandTotal ?? data?.amountDue ?? 0);
  const hasTax = taxTotal > 0.005;
  const currency = data?.currency || 'LKR';

  const availableCredit = Number(data?.externalCompanyCredit?.creditBalance || 0);
  const amountDue = Number(data?.amountDue || 0);
  const appliedCredit = applyCredit ? Number(creditToApply || 0) : 0;
  const netPayable = Math.max(0, amountDue - appliedCredit);
  const actualDeposit = customDeposit !== '' ? Number(customDeposit) : netPayable;
  const totalCovered = actualDeposit + appliedCredit;
  const excessAmount = Math.max(0, totalCovered - amountDue);
  const isFullCredit = appliedCredit > 0 && actualDeposit === 0;

  const pendingApprovalData = useMemo(() => {
    if (!pending || !data) return null;
    return {
      paymentMethod: data.paymentMethod || data.pendingPayment?.method,
      paymentReferenceNumber: data.paymentReferenceNumber || data.pendingPayment?.referenceNumber,
      paymentDate: data.paymentDate || data.pendingPayment?.paymentDate,
      paymentAmount: data.paymentAmount ?? data.pendingPayment?.amount ?? data.amountDue,
      creditAmountApplied: data.pendingPayment?.creditAmountApplied || 0,
      excessAmount: data.pendingPayment?.excessAmount || 0,
      receiptDocuments: data.receiptDocuments?.length
        ? data.receiptDocuments
        : (data.pendingPayment?.receiptDocuments || []),
      coordinatorEmail: data.guestEmail,
    };
  }, [pending, data]);

  const onPickReceipt = async (file) => {
    if (!file) {
      setReceipt(null);
      setReceiptMeta(null);
      return;
    }
    setUploading(true);
    try {
      const uploadRes = await uploadFilePublic(file, {
        category: 'EVENT_TICKET_PAYMENT_RECEIPT',
        description: `Event ticket payment receipt: ${data?.performaInvoice?.invoiceNumber || data?.guestName || ''}`.trim(),
        title: file.name,
      });
      const f = uploadRes?.data?.data || uploadRes?.data || {};
      setReceipt(file);
      setReceiptMeta({
        fileId: f.fileId || f._id || '',
        fileName: f.fileName || file.name,
        originalName: f.originalName || file.name,
        fileSize: f.fileSize || file.size,
        fileType: f.fileType || file.type,
      });
      toast.success('Receipt uploaded');
    } catch (err) {
      setReceipt(null);
      setReceiptMeta(null);
      toast.error(err?.response?.data?.message || err?.message || 'Receipt upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data?.amountDue) return toast.error('Amount due is missing');
    if (!isFullCredit && !form.referenceNumber.trim()) return toast.error('Reference number is required');
    if (!form.paymentDate.trim()) return toast.error('Payment date is required');
    if (!isFullCredit && !receiptMeta?.fileId) return toast.error('Payment receipt / slip is required');

    setBusy(true);
    try {
      await submitPublicEventTicketOfflinePayment(token, {
        method: isFullCredit ? 'CREDIT_BALANCE' : form.method,
        referenceNumber: form.referenceNumber.trim() || (isFullCredit ? 'CREDIT-SETTLEMENT' : ''),
        amount: actualDeposit,
        creditAmountApplied: appliedCredit,
        paymentDate: form.paymentDate,
        notes: form.notes,
        receiptDocuments: receiptMeta ? [receiptMeta] : [],
      });
      toast.success('Submitted for staff approval');
      setReceipt(null);
      setReceiptMeta(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const payByCard = async () => {
    setBusy(true);
    try {
      const res = await initiatePublicEventTicketCardPayment(token, { amount: Number(data.amountDue) });
      const url = res?.data?.data?.paymentPageUrl;
      if (!url) throw new Error('Payment page URL missing');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Card payment failed');
      setBusy(false);
    }
  };

  const downloadDoc = async (kind) => {
    setBusy(true);
    try {
      let res;
      let name;
      if (kind === 'proforma') {
        res = await downloadPublicEventTicketProformaPdf(token);
        name = 'Ticket_Proforma.pdf';
      } else if (kind === 'tax') {
        res = await downloadPublicEventTicketTaxInvoicePdf(token);
        name = 'Ticket_TaxInvoice.pdf';
      } else if (kind === 'invoice') {
        res = await downloadPublicEventTicketInvoicePdf(token);
        name = 'Ticket_Invoice.pdf';
      } else {
        res = await downloadPublicEventTicketReceiptPdf(token, 'latest');
        name = 'Ticket_Receipt.pdf';
      }
      downloadBlob(res.data, name);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-slate-600">Payment link not found.</div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100/70 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mx-auto max-w-6xl">
        {/* Top Branding / Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-stone-200/80 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 border border-amber-200">
                Official Payment Portal
              </span>
              <span className="text-xs text-stone-400">·</span>
              <span className="text-xs text-stone-500 font-medium">SLIM Events</span>
            </div>
            <h1 className="mt-1 font-serif text-2xl sm:text-3xl font-bold text-stone-900">
              {data.event?.name || 'Event Ticket Payment'}
            </h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="font-medium text-stone-700">256-bit Secure Checkout</span>
          </div>
        </div>

        {/* 2-Column Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ── Left Column: Order Summary & Info (5 Cols) ── */}
          <div className="lg:col-span-5 space-y-6">
            {/* Order Summary Card */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-2 border-b border-stone-100 pb-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-800/80">Order Summary</p>
                  <p className="mt-0.5 text-base font-semibold text-stone-900">
                    {data.externalCompanyName || data.guestName}
                  </p>
                  {data.guestEmail ? (
                    <p className="text-xs text-stone-500">{data.guestEmail}</p>
                  ) : null}
                </div>
                <div>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    paid
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : pending
                      ? 'bg-blue-50 text-blue-800 border-blue-200'
                      : rejected
                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                      : 'bg-amber-50 text-amber-800 border-amber-200'
                  }`}>
                    {data.paymentStatus || 'UNPAID'}
                  </span>
                </div>
              </div>

              {/* Line items if available */}
              {data.performaInvoice?.lineItems?.length > 0 && (
                <div className="space-y-2 border-b border-stone-100 pb-3">
                  <p className="text-xs font-semibold text-stone-700">Ticket Items</p>
                  <ul className="space-y-1.5 text-xs text-stone-600">
                    {data.performaInvoice.lineItems.map((l, i) => (
                      <li key={i} className="flex justify-between items-center bg-stone-50/70 px-2.5 py-1.5 rounded-lg border border-stone-100">
                        <span className="font-medium text-stone-800">{l.description} <span className="text-stone-500 font-normal">× {l.quantity}</span></span>
                        <span className="font-mono tabular-nums text-stone-900 font-semibold">{formatTicketMoney(l.amount, currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Financial Breakdown */}
              <div className="rounded-xl bg-stone-50 p-4 text-xs space-y-2 border border-stone-200/60">
                {subTotal > 0 && (
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotal</span>
                    <span className="tabular-nums font-mono">{formatTicketMoney(subTotal, currency)}</span>
                  </div>
                )}

                {hasTax && (
                  <>
                    {(invoice.taxLines || []).length > 0 ? (
                      (invoice.taxLines || []).map((line, idx) => (
                        <div key={idx} className="flex justify-between text-stone-500">
                          <span>{line.taxName || line.taxCode || 'Tax'}{line.percentage ? ` (${line.percentage}%)` : ''}</span>
                          <span className="tabular-nums font-mono">{formatTicketMoney(line.amount ?? 0, currency)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex justify-between text-stone-500">
                        <span>Tax{invoice.taxPercent ? ` (${invoice.taxPercent}%)` : ''}</span>
                        <span className="tabular-nums font-mono">{formatTicketMoney(taxTotal, currency)}</span>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-between border-t border-stone-200 pt-2.5 text-sm font-bold text-stone-900">
                  <span>Total Amount Due</span>
                  <span className="tabular-nums font-mono text-base text-amber-950">
                    {formatTicketMoney(grandTotal || data.amountDue, currency)}
                  </span>
                </div>
              </div>

              {/* Document Downloads */}
              <div className="space-y-1 pt-1">
                <p className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Billing Documents</p>
                <div className="flex flex-wrap gap-2">
                  {data.performaInvoice?.invoiceNumber ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadDoc('proforma')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 shadow-2xs"
                    >
                      <svg className="w-3.5 h-3.5 text-stone-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Proforma
                    </button>
                  ) : null}
                  {paid && hasTax ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadDoc('tax')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 shadow-2xs"
                    >
                      Download Tax Invoice
                    </button>
                  ) : null}
                  {paid && !hasTax && (data.invoice?.invoiceNumber || data.status === 'INVOICE_ISSUED') ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadDoc('invoice')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 shadow-2xs"
                    >
                      Download Invoice
                    </button>
                  ) : null}
                  {paid ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadDoc('receipt')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 shadow-2xs"
                    >
                      Download Official Receipt
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* SLIM Bank Details Card */}
            {(data.bankTransferDetails?.accountNumber || data.chequePayeeName) ? (
              <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm space-y-3">
                <div className="flex items-center justify-between border-b border-stone-100 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1 rounded bg-stone-100 text-stone-700">🏦</span>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-900">Official SLIM Bank Details</h3>
                  </div>
                  <span className="text-[10px] text-stone-500 font-mono">LKR Accounts</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {data.bankTransferDetails?.beneficiaryName && (
                    <div className="bg-stone-50 p-2.5 rounded-lg">
                      <span className="text-stone-400 block text-[10px] uppercase font-medium">Beneficiary</span>
                      <span className="font-semibold text-stone-800">{data.bankTransferDetails.beneficiaryName}</span>
                    </div>
                  )}
                  {data.bankTransferDetails?.beneficiaryBank && (
                    <div className="bg-stone-50 p-2.5 rounded-lg">
                      <span className="text-stone-400 block text-[10px] uppercase font-medium">Bank &amp; Branch</span>
                      <span className="font-semibold text-stone-800">{data.bankTransferDetails.beneficiaryBank}</span>
                    </div>
                  )}
                  {data.bankTransferDetails?.accountNumber && (
                    <div className="sm:col-span-2 bg-stone-50 p-2.5 rounded-lg flex items-center justify-between border border-stone-200/80">
                      <div>
                        <span className="text-stone-400 block text-[10px] uppercase font-medium">Account Number</span>
                        <span className="font-mono font-bold text-base text-stone-900 tracking-wider">
                          {data.bankTransferDetails.accountNumber}
                        </span>
                      </div>
                      <span className="text-[10px] bg-stone-200 text-stone-700 px-2 py-0.5 rounded font-medium">Primary</span>
                    </div>
                  )}
                  {data.chequePayeeName && (
                    <div className="sm:col-span-2 bg-stone-50 p-2.5 rounded-lg">
                      <span className="text-stone-400 block text-[10px] uppercase font-medium">Cheque Payable To</span>
                      <span className="font-semibold text-stone-800">{data.chequePayeeName}</span>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-stone-500 leading-relaxed pt-1">
                  Please include your order or company reference in the bank transfer remarks, upload your transfer receipt on the right, and submit for verification.
                </p>
              </div>
            ) : null}

            {/* Available Company Credit Card */}
            {availableCredit > 0 && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm text-xs text-emerald-800 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-bold uppercase tracking-wider text-emerald-950">Available Company Credit</span>
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 border border-emerald-200">
                    Active Balance
                  </span>
                </div>
                <p className="text-2xl font-bold font-mono text-emerald-700">
                  {formatTicketMoney(availableCredit, currency)}
                </p>
                <p className="text-emerald-700/90 text-[11px] leading-relaxed">
                  Your company has an unallocated credit balance. You can apply it directly towards this invoice to reduce or completely cover the amount payable.
                </p>
              </div>
            )}
          </div>

          {/* ── Right Column: Payment Form or Statuses (7 Cols) ── */}
          <div className="lg:col-span-7 space-y-6">
            {/* If Paid: Congratulations & Ticket Codes Card */}
            {paid && (
              <div className="rounded-2xl border border-emerald-200 bg-white p-6 sm:p-7 shadow-sm space-y-4">
                <div className="flex items-center gap-3 border-b border-emerald-100 pb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-lg">
                    ✓
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-emerald-900">Payment Complete</h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Your tickets and tax invoice have been issued and emailed to <strong>{data.guestEmail || 'your email'}</strong>.
                    </p>
                  </div>
                </div>

                {Array.isArray(data.tickets) && data.tickets.length > 0 ? (
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold text-stone-800">Your Issued Event Tickets ({data.tickets.length})</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {data.tickets.map((t) => (
                        <li key={t.code} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-stone-50/60 shadow-2xs">
                          <TicketCodeQr code={t.code} size={64} showDownload />
                          <div className="min-w-0 flex-1 text-xs">
                            <span className="font-mono font-bold block text-sm text-stone-900">{t.code}</span>
                            <span className="text-stone-600 font-medium block truncate">{t.ticketTypeName}</span>
                            {t.seatNumber != null ? <span className="text-stone-900 font-semibold block">Seat No. {t.seatNumber}</span> : null}
                            <span className="text-[10px] text-stone-400 mt-1 block">Present at door check-in</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {data.transfersEnabled ? (
                  <div className="pt-2 border-t border-stone-100 flex justify-between items-center text-xs">
                    <span className="text-stone-600">Need to reassign or transfer a ticket?</span>
                    <Link
                      className="font-semibold text-amber-900 hover:text-amber-800 underline"
                      to={`/public/events/${data.event?.id || data.event?._id}/tickets/transfer`}
                    >
                      Start Ticket Transfer →
                    </Link>
                  </div>
                ) : null}
              </div>
            )}

            {/* If Pending Approval Card */}
            {pending && pendingApprovalData ? (
              <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm">
                <PaymentPendingApprovalCard data={pendingApprovalData} contactEmail={data.guestEmail} />
              </div>
            ) : null}

            {/* If Rejected Card */}
            {rejected && canShowPaymentForm ? (
              <div className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm">
                <PaymentRejectedCard data={{ rejectionReason: data.rejectionReason, rejectedAt: data.rejectedAt }} />
              </div>
            ) : null}

            {linkExpired ? (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-900" data-testid="payment-link-expired">
                <p className="font-semibold">This payment link has expired</p>
                <p className="mt-1">
                  Please contact the event organiser to extend it
                  {data.paymentLinkExpiresAt ? ` (it expired on ${new Date(data.paymentLinkExpiresAt).toLocaleDateString()})` : ''}.
                  Once extended, this same link will work again.
                </p>
              </div>
            ) : null}

            {/* Payment Method & Submission Form */}
            {canShowPaymentForm && (
              <div className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-7 shadow-sm space-y-5">
                <div>
                  <h3 className="text-lg font-bold text-stone-900">Make Payment</h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Select your preferred payment method and submit your transaction details.
                  </p>
                </div>

                {/* Mode Selector Tabs */}
                <div className="flex rounded-xl bg-stone-100 p-1 border border-stone-200/80">
                  <button
                    type="button"
                    onClick={() => setPayMode('bank')}
                    className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-all ${
                      payMode === 'bank'
                        ? 'bg-white text-stone-900 shadow-sm'
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    Bank Transfer / Cheque Deposit
                  </button>
                  {data.cardPaymentsEnabled ? (
                    <button
                      type="button"
                      onClick={() => setPayMode('card')}
                      className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-all ${
                        payMode === 'card'
                          ? 'bg-white text-stone-900 shadow-sm'
                          : 'text-stone-600 hover:text-stone-900'
                      }`}
                    >
                      Credit / Debit Card (Paycorp)
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      title="Card payments are not available for event tickets. Please pay by bank transfer."
                      className="flex-1 cursor-not-allowed rounded-lg py-2 text-xs font-semibold text-stone-400"
                    >
                      Credit / Debit Card (unavailable)
                    </button>
                  )}
                </div>

                {/* Card Payment View */}
                {payMode === 'card' && data.cardPaymentsEnabled ? (
                  <div className="space-y-4 rounded-xl bg-stone-50 p-5 border border-stone-200 text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-800 mx-auto flex items-center justify-center text-xl">
                      💳
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-stone-900">Secure Online Card Payment</h4>
                      <p className="text-xs text-stone-600 mt-1 max-w-sm mx-auto">
                        Pay instantly via Paycorp gateway. Your tickets, QR codes, and tax invoice will be issued immediately upon transaction approval.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={payByCard}
                      className="w-full sm:w-auto px-8 rounded-xl bg-stone-900 py-3 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-50 shadow-sm"
                    >
                      {busy ? 'Connecting to Gateway…' : `Pay ${formatTicketMoney(data.amountDue, currency)} Now`}
                    </button>
                  </div>
                ) : null}

                {/* Bank / Offline Transfer Form */}
                {payMode === 'bank' ? (
                  <form className="space-y-4" onSubmit={submit}>
                    {/* Credit Application Toggle */}
                    {availableCredit > 0 && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                        <label className="flex items-center gap-2.5 cursor-pointer text-xs font-semibold text-emerald-950">
                          <input
                            type="checkbox"
                            checked={applyCredit}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setApplyCredit(checked);
                              if (checked) {
                                const init = Math.min(availableCredit, amountDue);
                                setCreditToApply(init);
                                setCustomDeposit(String(Math.max(0, amountDue - init)));
                              } else {
                                setCreditToApply(0);
                                setCustomDeposit('');
                              }
                            }}
                            className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500 w-4 h-4"
                          />
                          <span>Apply Company Credit Balance ({formatTicketMoney(availableCredit, currency)})</span>
                        </label>

                        {applyCredit && (
                          <div className="space-y-2 pt-2 border-t border-emerald-200 text-xs">
                            <label className="block text-[11px] font-medium text-emerald-900">
                              Credit Amount to Settle (Max: {formatTicketMoney(Math.min(availableCredit, amountDue), currency)})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={Math.min(availableCredit, amountDue)}
                              value={creditToApply}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                const num = isNaN(val) ? 0 : Math.max(0, Math.min(Math.min(availableCredit, amountDue), val));
                                setCreditToApply(num);
                                setCustomDeposit(String(Math.max(0, amountDue - num)));
                              }}
                              className="w-full rounded-lg border border-emerald-300 px-3 py-2 text-sm bg-white font-mono"
                            />
                            <div className="flex justify-between items-center text-xs text-emerald-950 font-semibold bg-white p-2.5 rounded-lg border border-emerald-200">
                              <span>Remaining Bank Transfer Required:</span>
                              <span className="font-mono text-sm">{formatTicketMoney(netPayable, currency)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Excess Payment Alert */}
                    {excessAmount > 0 && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs text-amber-900 space-y-1">
                        <p className="font-bold flex items-center gap-1.5">
                          <span>⚠️ Excess Payment Detected (+{formatTicketMoney(excessAmount, currency)})</span>
                        </p>
                        <p className="text-[11px] text-amber-800 leading-relaxed">
                          Your deposit exceeds the order balance. Upon finance verification, the excess of {formatTicketMoney(excessAmount, currency)} will automatically be credited to your company profile for future purchases.
                        </p>
                      </div>
                    )}

                    {/* Form Fields Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                      <label className="font-medium text-stone-700">
                        Payment Method <span className="text-rose-500">*</span>
                        {isFullCredit ? (
                          <div className="mt-1 w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 font-semibold">
                            Credit Balance (100% Settled)
                          </div>
                        ) : (
                          <select
                            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-xs bg-white text-stone-900 shadow-2xs focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                            value={form.method}
                            onChange={(e) => setForm((s) => ({ ...s, method: e.target.value }))}
                          >
                            <option value="BANK_TRANSFER">Bank Transfer (SLT / Wire)</option>
                            <option value="CHEQUE_DEPOSIT">Cheque Deposit</option>
                          </select>
                        )}
                      </label>

                      <label className="font-medium text-stone-700">
                        Payment Date <span className="text-rose-500">*</span>
                        <input
                          required
                          type="date"
                          className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-xs bg-white text-stone-900 shadow-2xs focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                          value={form.paymentDate}
                          onChange={(e) => setForm((s) => ({ ...s, paymentDate: e.target.value }))}
                        />
                      </label>

                      {!isFullCredit && (
                        <label className="font-medium text-stone-700">
                          Reference / Cheque Number <span className="text-rose-500">*</span>
                          <input
                            required
                            placeholder="e.g. TXN-8934201 or Cheque #10294"
                            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-xs bg-white text-stone-900 shadow-2xs focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                            value={form.referenceNumber}
                            onChange={(e) => setForm((s) => ({ ...s, referenceNumber: e.target.value }))}
                          />
                        </label>
                      )}

                      <label className="font-medium text-stone-700">
                        {isFullCredit ? 'Amount Settled via Credit' : 'Amount Deposited / Transferred'} <span className="text-rose-500">*</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={String(netPayable)}
                          className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-xs font-mono bg-white text-stone-900 shadow-2xs focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                          value={customDeposit !== '' ? customDeposit : netPayable}
                          onChange={(e) => setCustomDeposit(e.target.value)}
                          disabled={isFullCredit}
                        />
                      </label>
                    </div>

                    {/* Receipt Dropzone */}
                    {!isFullCredit && (
                      <div className="pt-1">
                        <ProofAttachmentDropzone
                          label="Payment Receipt / Deposit Slip (PDF, JPG, PNG)"
                          hint="Clear photograph or digital copy of the transfer slip or bank deposit receipt"
                          required
                          busy={uploading}
                          disabled={busy || uploading}
                          file={receipt || receiptMeta}
                          onChange={onPickReceipt}
                          className="text-xs text-stone-600"
                        />
                      </div>
                    )}

                    {/* Notes */}
                    <label className="block text-xs font-medium text-stone-700">
                      Additional Notes for Finance (Optional)
                      <textarea
                        placeholder="e.g. Deposited from Dialog Axiata corporate account"
                        className="mt-1 w-full rounded-lg border border-stone-300 p-2.5 text-xs bg-white text-stone-900 shadow-2xs focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                        rows={2}
                        value={form.notes}
                        onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                      />
                    </label>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={busy || uploading || (!isFullCredit && !receiptMeta?.fileId)}
                        className="w-full rounded-xl bg-stone-900 py-3 text-sm font-bold text-white hover:bg-stone-800 disabled:opacity-50 shadow-md transition-colors"
                      >
                        {busy
                          ? 'Submitting Payment Proof…'
                          : isFullCredit
                          ? 'Settle Order via Credit Balance'
                          : `Submit Payment of ${formatTicketMoney(actualDeposit, currency)}`}
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicEventTicketPaymentPage;
